import {
	CollectionModelBase,
	CollectionModelFieldHeight,
	CollectionModelFieldSubFormInstance,
	IkeCalcVersion,
} from '@ikegps/core-shared';
import type { FieldJoin } from './field.join';

export abstract class IkeCalcFieldJoin implements FieldJoin {
	abstract type: string;
	subformInstanceID: string;

	field: CollectionModelFieldHeight;
	wire: CollectionModelFieldSubFormInstance;
	circuit: CollectionModelFieldSubFormInstance;
	span: CollectionModelFieldSubFormInstance;

	constructor(field: CollectionModelFieldHeight) {
		this.field = field;
		this.wire = field.parent;

		const form = this.wire.schema.getForm();
		const parentSubFormInstance = this.wire.parent.parent as CollectionModelFieldSubFormInstance;

		if (form.metadata.import.version >= IkeCalcVersion.V0_3_0) {
			this.circuit = this.wire;
			this.span = parentSubFormInstance;
		} else {
			// IkeCalc V0.2.x
			this.circuit = parentSubFormInstance;
			this.span = this.circuit.parent.parent as CollectionModelFieldSubFormInstance;
		}

		this.subformInstanceID = this.wire.instanceId;
	}

	abstract joins(other: FieldJoin): boolean;

	getTitle(): string {
		return `${this.span.title} (${this.span.formTitle})`;
	}

	protected getFieldValue(subform: CollectionModelBase, fieldId: string): string | null {
		if (subform == null || !subform.hasField(fieldId)) {
			return null;
		}

		const field = subform.getField(fieldId);
		if (!field.hasValue()) {
			return null;
		}

		return field.displayValue;
	}

	protected fieldsEqual(
		subformA: CollectionModelFieldSubFormInstance,
		subformB: CollectionModelFieldSubFormInstance,
		fieldID: string
	): boolean {
		const fieldAValue = this.getFieldValue(subformA, fieldID);
		const fieldBValue = this.getFieldValue(subformB, fieldID);

		return fieldAValue != null && fieldBValue != null && fieldAValue === fieldBValue;
	}

	protected spansOpposing(
		spanA: CollectionModelFieldSubFormInstance,
		spanB: CollectionModelFieldSubFormInstance
	): boolean {
		if (spanA.instanceId === spanB.instanceId) {
			return false;
		}

		if (!spanA.hasField('spanType') || !spanB.hasField('spanType')) {
			return false;
		}

		const spanAType = spanA.getField('spanType').value;
		const spanBType = spanB.getField('spanType').value;

		if (spanAType == null || spanBType == null) {
			return false;
		}

		if (spanAType === 'NEXT_POLE') {
			return spanBType === 'PREVIOUS_POLE';
		}

		if (spanAType === 'PREVIOUS_POLE') {
			return spanBType === 'NEXT_POLE';
		}

		return spanAType === spanBType;
	}

	protected isDeadEnd(value: string): boolean {
		return value != null && value.match(/dead[-_ ]*end/i) != null;
	}
}
